import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Grid,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import moment from "moment";
import React, { useEffect, useState } from "react";
import type {
	ParameterFormState,
	SPParameter,
} from "../models/SPExecutorModels";

type Props = {
	parameters: SPParameter[];
	onExecute: (params: ParameterFormState[]) => void;
	isExecuting: boolean;
};

const getDefaultValue = (dataType: string): any => {
	const type = dataType.toLowerCase();
	if (
		[
			"int",
			"bigint",
			"smallint",
			"tinyint",
			"decimal",
			"float",
			"real",
			"money",
			"numeric",
		].includes(type)
	) {
		return "";
	}
	if (["datetime", "datetime2", "date", "smalldatetime"].includes(type)) {
		return null;
	}
	if (type === "bit") {
		return false;
	}
	return "";
};

const renderInput = (
	param: ParameterFormState,
	onChange: (value: any) => void,
	disabled: boolean,
) => {
	const type = param.dataType.toLowerCase();

	if (["int", "bigint", "smallint", "tinyint"].includes(type)) {
		return (
			<TextField
				type="number"
				label={param.parameterName}
				value={param.value}
				onChange={(e) =>
					onChange(e.target.value === "" ? "" : Number(e.target.value))
				}
				disabled={disabled}
				fullWidth
				size="small"
				inputProps={{ step: 1 }}
			/>
		);
	}

	if (["decimal", "float", "real", "money", "numeric"].includes(type)) {
		return (
			<TextField
				type="number"
				label={param.parameterName}
				value={param.value}
				onChange={(e) =>
					onChange(e.target.value === "" ? "" : Number(e.target.value))
				}
				disabled={disabled}
				fullWidth
				size="small"
				inputProps={{ step: "any" }}
			/>
		);
	}

	if (["datetime", "datetime2", "date", "smalldatetime"].includes(type)) {
		return (
			<DateTimePicker
				label={param.parameterName}
				value={param.value ? moment(param.value) : null}
				onChange={(newValue) => onChange(newValue?.toDate() || null)}
				disabled={disabled}
				slotProps={{
					textField: {
						fullWidth: true,
						size: "small",
					},
				}}
			/>
		);
	}

	if (type === "bit") {
		return (
			<FormControlLabel
				control={
					<Checkbox
						checked={!!param.value}
						onChange={(e) => onChange(e.target.checked)}
						disabled={disabled}
					/>
				}
				label={param.parameterName}
			/>
		);
	}

	// Default: text field for varchar, nvarchar, char, etc.
	return (
		<TextField
			label={param.parameterName}
			value={param.value}
			onChange={(e) => onChange(e.target.value)}
			disabled={disabled}
			fullWidth
			size="small"
			multiline={type.includes("text") || type.includes("max")}
			rows={type.includes("text") || type.includes("max") ? 3 : 1}
		/>
	);
};

export const DynamicParameterForm = ({
	parameters,
	onExecute,
	isExecuting,
}: Props) => {
	const [formState, setFormState] = useState<ParameterFormState[]>([]);

	useEffect(() => {
		const initialState: ParameterFormState[] = parameters.map((p) => ({
			parameterName: p.ParameterName,
			include: !p.IsOptional, // Required params start included and locked
			sendAsNull: false,
			value: getDefaultValue(p.DataType),
			dataType: p.DataType,
			isOptional: p.IsOptional,
		}));
		setFormState(initialState);
	}, [parameters]);

	const updateParam = (
		index: number,
		field: keyof ParameterFormState,
		value: any,
	) => {
		setFormState((prev) => {
			const updated = [...prev];
			updated[index] = { ...updated[index], [field]: value };

			// If "sendAsNull" is checked, include must be true
			if (field === "sendAsNull" && value === true) {
				updated[index].include = true;
			}

			// If "include" is unchecked, sendAsNull must be false
			if (field === "include" && value === false) {
				updated[index].sendAsNull = false;
			}

			return updated;
		});
	};

	const handleExecute = () => {
		onExecute(formState);
	};

	if (parameters.length === 0) {
		return (
			<Paper sx={{ p: 2 }}>
				<Typography variant="body2" color="text.secondary">
					This stored procedure has no parameters.
				</Typography>
				<Button
					variant="contained"
					onClick={handleExecute}
					disabled={isExecuting}
					sx={{ mt: 2 }}
				>
					{isExecuting ? "Executing..." : "Execute"}
				</Button>
			</Paper>
		);
	}

	return (
		<Paper sx={{ p: 2 }}>
			<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
				Parameters
			</Typography>
			<Grid container spacing={2}>
				{formState.map((param, index) => {
					const isInputDisabled = !param.include || param.sendAsNull;
					const isIncludeDisabled = !param.isOptional; // Required params can't be unchecked

					return (
						<Grid item xs={12} key={param.parameterName}>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 2,
									p: 1,
									borderRadius: 1,
									bgcolor: param.include ? "transparent" : "action.hover",
								}}
							>
								<FormControlLabel
									control={
										<Checkbox
											checked={param.include}
											onChange={(e) =>
												updateParam(index, "include", e.target.checked)
											}
											disabled={isIncludeDisabled}
											size="small"
										/>
									}
									label="Include"
									sx={{ minWidth: 100 }}
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={param.sendAsNull}
											onChange={(e) =>
												updateParam(index, "sendAsNull", e.target.checked)
											}
											disabled={!param.include}
											size="small"
										/>
									}
									label="NULL"
									sx={{ minWidth: 80 }}
								/>
								<Box sx={{ flexGrow: 1 }}>
									{renderInput(
										param,
										(value) => updateParam(index, "value", value),
										isInputDisabled,
									)}
								</Box>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ minWidth: 80 }}
								>
									{param.dataType}
									{!param.isOptional && (
										<Typography component="span" color="error" sx={{ ml: 0.5 }}>
											*
										</Typography>
									)}
								</Typography>
							</Box>
						</Grid>
					);
				})}
			</Grid>
			<Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
				<Button
					variant="contained"
					onClick={handleExecute}
					disabled={isExecuting}
					size="large"
				>
					{isExecuting ? "Executing..." : "Execute"}
				</Button>
			</Box>
		</Paper>
	);
};
